import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DeactivateCustomerProductModal } from 'src/components/customer-product/deactivate/DeactivateCustomerProductModal'
import type {
  CustomerProductFormMode,
  CustomerProductFormValues,
} from 'src/components/customer-product/product-form/CustomerProductForm.interface'
import { CustomerProductFormModal } from 'src/components/customer-product/product-form/CustomerProductFormModal'
import { CustomerProductsTable } from 'src/components/customer-product/products-table/CustomerProductsTable'
import { CustomerProductsTableToolbar } from 'src/components/customer-product/products-table/CustomerProductsTableToolbar'
import { Button } from 'src/components/shared/Button'
import {
  AdminCreateCustomerProduct,
  AdminDeactivateCustomerProduct,
  AdminFetchCustomerProducts,
  AdminUpdateCustomerProduct,
} from 'src/constants/actionTypes'
import type { CustomerProductName } from 'src/constants/customer/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { getCustomerProducts, getCustomerProductSaving } from 'src/selectors/customerProduct'
import {
  getAvailableProductsToAdd,
  serializeCustomerProductTimestamp,
} from 'src/utils/customerProductList'
import { GSIcon } from 'src/components/shared/GSIcon'

interface FormModalState {
  mode: CustomerProductFormMode
  product: CustomerProduct | null
}

const ALL_PRODUCTS_ACTIVE_REASON =
  'Every product is already active for this customer. Deactivate one to add it again.'

const CustomerProductsPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [formModalState, setFormModalState] = useState<FormModalState | null>(null)
  const [deactivatingProduct, setDeactivatingProduct] = useState<CustomerProduct | null>(null)

  const products = useSelector(getCustomerProducts)
  const isSaving = useSelector(getCustomerProductSaving)
  // Add, Edit end time and Deactivate all call `partner_products_manage` endpoints.
  const canManage = useHasPermission(InternalPermission.PartnerProductsManage)

  /**
   * Guards ONE thing: the post-write refetch. A mutation dispatched for this customer can
   * resolve after the admin has switched away — the layout unmounts this page on a switch, but
   * the awaited continuation still runs, and `handleRefresh` would then dispatch a fetch for
   * the OLD customer, re-pointing the shared slice and leaving the new customer's tabs gated
   * shut with nothing to recover them.
   *
   * It deliberately does NOT gate the success toast or the modal close. Those are `setState`
   * calls, which are no-ops on an unmounted component anyway, and gating them meant that any
   * unmount/remount left the modal stuck open with no toast and no refreshed list — the write
   * had succeeded, but nothing on screen said so.
   *
   * Reset on mount, not just cleared on cleanup: a ref survives a remount of the same element,
   * so a cleanup-only version latches `false` permanently the first time this page is unmounted
   * and re-shown.
   */
  const isMountedRef = useRef<boolean>(true)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const availableProducts = useMemo<CustomerProductName[]>(() => {
    return getAvailableProductsToAdd(products)
  }, [products])

  // No fetch effect: CustomerLayout dispatches the products fetch on customer select and
  // withholds this page until it settles, because feature gates read the same slice. Refresh
  // therefore re-dispatches the shared fetch rather than bumping a page-local token.
  //
  // Silent, like the layout's own fetch: the layout owns this data and surfaces a failure in
  // its banner, so a toast on top of that would report the same failure twice.
  const handleRefresh = useCallback(() => {
    if (!customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCustomerProducts.request({ partnerId: customerId }), true)
  }, [asyncDispatchWithErrorToast, customerId])

  const handleOpenCreate = useCallback(() => {
    setFormModalState({ mode: 'create', product: null })
  }, [])

  const handleOpenEdit = useCallback((product: CustomerProduct) => {
    setFormModalState({ mode: 'edit', product })
  }, [])

  const handleCloseFormModal = useCallback(() => {
    setFormModalState(null)
  }, [])

  const handleOpenDeactivate = useCallback((product: CustomerProduct) => {
    setDeactivatingProduct(product)
  }, [])

  const handleCloseDeactivate = useCallback(() => {
    setDeactivatingProduct(null)
  }, [])

  const handleSubmit = useCallback(
    async (values: CustomerProductFormValues) => {
      if (!formModalState || !customerId) {
        return
      }

      if (formModalState.mode === 'edit') {
        if (!formModalState.product || !values.endTime) {
          return
        }

        const updated = await asyncDispatchWithErrorToast<CustomerProduct>(
          AdminUpdateCustomerProduct.request({
            partnerId: customerId,
            productId: formModalState.product.id,
            endTime: serializeCustomerProductTimestamp(values.endTime),
          }),
        )

        if (!updated) {
          return
        }

        snackbar?.enqueueSnackbar('Product updated', { variant: 'success' })
        setFormModalState(null)

        // Refetch only while this page is still the one on screen — see `isMountedRef`.
        if (isMountedRef.current) {
          handleRefresh()
        }

        return
      }

      if (values.product === '' || !values.startTime) {
        return
      }

      const created = await asyncDispatchWithErrorToast<CustomerProduct>(
        AdminCreateCustomerProduct.request({
          partnerId: customerId,
          product: values.product,
          startTime: serializeCustomerProductTimestamp(values.startTime),
          // An absent key is what "ongoing" means to the backend.
          endTime: values.endTime ? serializeCustomerProductTimestamp(values.endTime) : undefined,
        }),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Product added', { variant: 'success' })
      setFormModalState(null)

      // Refetch only while this page is still the one on screen — see `isMountedRef`.
      if (isMountedRef.current) {
        handleRefresh()
      }
    },
    [formModalState, customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleConfirmDeactivate = useCallback(async () => {
    if (!deactivatingProduct || !customerId) {
      return
    }

    const deactivated = await asyncDispatchWithErrorToast<CustomerProduct>(
      AdminDeactivateCustomerProduct.request({
        partnerId: customerId,
        product: deactivatingProduct.product,
        productId: deactivatingProduct.id,
      }),
    )

    if (!deactivated) {
      return
    }

    snackbar?.enqueueSnackbar('Product deactivated', { variant: 'success' })
    setDeactivatingProduct(null)

    // Refetch only while this page is still the one on screen — see `isMountedRef`.
    if (isMountedRef.current) {
      handleRefresh()
    }
  }, [deactivatingProduct, customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  const canAddProduct = availableProducts.length > 0

  return (
    <Fragment>
      <div data-testid="admin-customer-products-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">Products</Typography>
          </div>
          {/* Hidden without `partner_products_manage`. With it, disabled when every product is
              active: the Add form's select would be empty, and POST answers 409 for an
              already-active product. */}
          {canManage ? (
            <Tooltip title={canAddProduct ? '' : ALL_PRODUCTS_ACTIVE_REASON}>
              <span>
                <Button
                  size="small"
                  color="dark"
                  disabled={!canAddProduct}
                  leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
                  onClick={handleOpenCreate}
                  data-testid="customer-products-add-button"
                >
                  Add product
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </div>
        <CustomerProductsTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <CustomerProductsTable
          searchQuery={searchQuery}
          canManage={canManage}
          onEditProduct={handleOpenEdit}
          onDeactivateProduct={handleOpenDeactivate}
        />
      </div>
      <CustomerProductFormModal
        isOpen={Boolean(formModalState)}
        mode={formModalState?.mode ?? 'create'}
        product={formModalState?.product ?? null}
        availableProducts={availableProducts}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseFormModal}
      />
      <DeactivateCustomerProductModal
        isOpen={Boolean(deactivatingProduct)}
        product={deactivatingProduct}
        isSaving={isSaving}
        onConfirm={handleConfirmDeactivate}
        onClose={handleCloseDeactivate}
      />
    </Fragment>
  )
}

export const CustomerProductsPage = memo(CustomerProductsPageComponent)
